import _ from 'underscore';
import Backbone from 'backbone';
import ViewModel, {toggleState} from '@bingads-webui-component-legacy/toggle/viewmodel/index';
import defaultTemplate from '@bingads-webui-component-legacy/toggle/template/toggle.pug';

/*
* @constant
* @type {string}
* aria-checked state
*/
const checkedState = {
  on: 'true',
  indeterminate: 'mixed',
  off: 'false',
};

/*
* @constant
* @type {string}
* Default toggle text
*/
const defaultToggleText = {
  on: 'On',
  indeterminate: 'Indeterminate',
  off: 'Off',
};

/* @this ToggleView */
function redraw() {
  _.each(toggleState, (state) => this.$el.removeClass(`toggle-${state}`));
  this.$el.addClass(`toggle-${this.viewModel.state}`);

  if (this.viewModel.enabled) {
    this.$el.removeClass('disabled');
  } else {
    this.$el.addClass('disabled');
  }

  this.$el.attr('aria-pressed', checkedState[this.viewModel.state]);
  this.$el.attr('aria-labelledby', this.labelledbyId);
  this.$('.toggle-text').text(this.toggleText[this.viewModel.state]);
}

/**
* Toggle view
* @extends Backbone.View
*/
export default class ToggleView extends Backbone.View {
  /**
  * Backbone view initialization.
  * @param  {object}    [options]             The initialization options
  * @param  {string}    [options.mode]        "default": Toggle in default mode; "triState": Toggle in triple state mode
  * @param  {string}    [options.state]       The initial toggle state. "on", "indeterminate" if in triState, "off"
  * @param  {string}    [options.enabled]     The initial toggle status. "true" or "false"
  * @param  {function}  [options.template]    The template to display toggle
  * @param  {object}    [options.toggleText]  The toggle text dictionary
  * @param  {function}  [options.onchange]     The handler for change event
  * @return {void}
  */
  initialize(options = {}) {
    this.options = options;
    this.viewModel = new ViewModel(_.extend({}, this.options, {
      onchange: () => {
        this.trigger('change', this.viewModel.state);
        redraw.call(this);
      },
    }));
    this.template = this.options.template || defaultTemplate;
    this.toggleText = this.options.toggleText || defaultToggleText;
    this.labelledbyId = this.options.labelledbyId || '';
  }

  get tagName() {
    return 'a';
  }

  get attributes() {
    return {
      href: 'javascript:;', // eslint-disable-line no-script-url
      role: 'button',
    };
  }

  get events() {
    return {click: this.toggle};
  }

  /**
  * Render the view.
  * @return {ToggleView}    This object.
  */
  render() {
    this.$el.addClass('toggle');
    this.$el.html(this.template());
    redraw.call(this);

    return this;
  }

  /**
  * Toggle the toggle.
  * @return {void}
  */
  toggle() {
    this.viewModel.toggle();
  }

  /**
  * Set state of the toggle.
  * @param  {string}  [state]  "on", "indeterminate" if in triState, "off"
  * @return {void}
  */
  setToggleState(state) {
    this.viewModel.setToggleState(state);
  }

  /**
  * Set toggle mode.
  * @param  {string}  [mode]  "default": Toggle in default mode; "triState": Toggle in triple state mode
  * @return {void}
  */
  setMode(mode) {
    this.viewModel.reset({mode}, redraw.bind(this));
  }

  /**
  * Enable toggle.
  * @return {void}
  */
  enable() {
    this.viewModel.reset({enabled: true}, redraw.bind(this));
  }

  /**
  * Disable toggle.
  * @return {void}
  */
  disable() {
    this.viewModel.reset({enabled: false}, redraw.bind(this));
  }
}
